import { useCallback, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { SubjectResponse } from '@/api/types/Subject'
import { statesData, type StateData } from '@/utils/stateData'

interface OrganizationIndividualFormType {
  avatar?: File | null
  previousAvatar?: string
  cover?: File | null
  previousCover?: string
  firstName: SubjectResponse['user']['details']['firstName']
  lastName?: SubjectResponse['user']['details']['lastName']
  email: SubjectResponse['user']['email']
  city?: SubjectResponse['user']['details']['city']
  state?: StateData['state']
  groupName?: string,
  specialty?: SubjectResponse['details']['occupation']
}

const useOrganizationIndividualForm = (individual: Partial<SubjectResponse> | null) => {
  const defaultValues = useCallback((i: Partial<SubjectResponse> | null): OrganizationIndividualFormType => ({
    avatar: undefined,
    previousAvatar: i?.user?.assets?.PROFILE_PICTURE || '',
    cover: undefined,
    previousCover: i?.user?.assets?.COVER || '',
    firstName: i?.user?.details?.firstName || '',
    lastName: i?.user?.details?.lastName || '',
    email: i?.user?.email || '',
    city: i?.user?.details?.city || '',
    state: i?.user?.details?.state || '',
    groupName: i?.group?.name || '',
    specialty: i?.details?.occupation || '',
  }), [])

  const validationSchema = useMemo(() => z.object({
    avatar: z.instanceof(File).nullish(),
    cover: z.instanceof(File).nullish(),
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().optional(),
    email: z.email('Invalid email address'),
    city: z.string().optional(),
    state: z.string().refine(
      val => !val || statesData.some(s => s.state === val),
      { message: 'Invalid state' }
    ).optional(),
    groupName: z.string().optional(),
    specialty: z.string().nullish(),
  }), [])

  const formDefaults = useMemo(() => defaultValues(individual), [individual, defaultValues])

  const form = useForm<OrganizationIndividualFormType>({
    defaultValues: formDefaults,
    resolver: zodResolver(validationSchema),
  })

  const resetForm = useCallback((i: Partial<SubjectResponse> | null) => {
    form.reset(defaultValues(i))
  }, [form, defaultValues])

  return { form, defaultValues, resetForm }
}

export default useOrganizationIndividualForm
